import * as React from 'react';

export interface UseTouchSelectSearchProps<K extends string | number> {
  options?: Array<{
    label: React.ReactNode;
    subLabel?: React.ReactNode;
    extraInfo?: React.ReactNode;
    group?: string;
    selectedLabel?: string;
    searchable?: string;
    key: K;
    isDisabled?: boolean;
  }>;
  extractTextContentFromReactNode: (node: React.ReactNode) => string;
}

export interface FilteredOption {
  id: string | number | undefined;
  label: React.ReactNode;
  subLabel?: React.ReactNode;
  rightInfo?: React.ReactNode;
  group?: string;
  isDisabled?: boolean;
}

export const useTouchSelectSearch = <K extends string | number>({
  options,
  extractTextContentFromReactNode,
}: UseTouchSelectSearchProps<K>) => {
  const [query, setQuery] = React.useState('');

  const normalizedOptions = React.useMemo(
    () =>
      options?.map((it) => {
        const normalizedLabelText = extractTextContentFromReactNode(it.label)
          .toLowerCase()
          .replace(/-/g, '');

        return {
          ...it,
          searchable: [it.searchable, normalizedLabelText]
            .filter(Boolean)
            .join(' '),
        };
      }),
    [options, extractTextContentFromReactNode],
  );

  const filteredOptions = React.useMemo(() => {
    const normalizedQuery = query.toLowerCase().replace(/-/g, '');
    const queryTerms = normalizedQuery.split(' ').filter(Boolean);

    return (
      normalizedOptions
        ?.filter((it) =>
          queryTerms.every((term) => it.searchable.includes(term)),
        )
        .map((it) => ({
          id: it.key,
          label: it.label,
          subLabel: it.subLabel,
          rightInfo: it.extraInfo,
          group: it.group,
          isDisabled: it.isDisabled,
        })) ?? []
    );
  }, [query, normalizedOptions]);

  const clearQuery = React.useCallback(() => {
    setQuery('');
  }, []);

  const updateQuery = React.useCallback((newQuery: string) => {
    setQuery(newQuery);
  }, []);

  return {
    query,
    setQuery: updateQuery,
    clearQuery,
    filteredOptions,
    normalizedOptions,
  };
};
